import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    FileText,
    Receipt,
    HandCoins,
    LockKey,
    ArrowUpRight,
    WarningOctagon,
    Clock,
    Heartbeat,
    TrendDown,
    TrendUp,
    Minus,
    Users,
    UserCircle,
    Briefcase,
    Presentation,
    ArrowsClockwise,
    CalendarBlank,
    Coins,
    Hourglass,
    Stack,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import StatusBadge from "../components/StatusBadge";
import MonthSummaryCard from "../components/regularisation/MonthSummaryCard";
import { toast } from "sonner";

const Stat = ({ eyebrow, value, hint, icon, href, testId, valueClass = "", tone }) => {
    const toneClass =
        tone === "danger"
            ? "border-rose-200 bg-rose-50/40 hover:border-rose-400"
            : tone === "warning"
                ? "border-amber-200 bg-amber-50/40 hover:border-amber-400"
                : "hover:border-slate-900";
    const Inner = (
        <div className={"reedos-card p-5 group transition-colors " + toneClass} data-testid={testId}>
            <div className="flex items-start justify-between gap-3">
                <div className="reedos-eyebrow">{eyebrow}</div>
                {icon && <div className="text-slate-400 group-hover:text-slate-900 transition-colors">{icon}</div>}
            </div>
            <div className={"font-display font-bold mt-3 reedos-data leading-none " + valueClass}>{value}</div>
            {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
        </div>
    );
    return href ? <Link to={href} className="block">{Inner}</Link> : Inner;
};

function VendorComplianceCards({ data, loading }) {
    const fmtINR = (v) => formatINR(v ?? 0, { compact: true });
    const cards = [
        {
            eyebrow: "Vendor GST Credits Available",
            value: data?.vendor_gst_credits_available?.amount,
            count: data?.vendor_gst_credits_available?.count || 0,
            hint: `Input Tax Credit (FY ${data?.fy || ""})`,
            href: "/reports/vendor-compliance/input-gst",
            icon: <Coins size={18} weight="fill" />,
            tone: "indigo",
            testId: "stat-vendor-gst-credits",
        },
        {
            eyebrow: "Pending Vendor TDS Deposits",
            value: data?.pending_vendor_tds_deposits?.amount,
            count: data?.pending_vendor_tds_deposits?.count || 0,
            hint: `Awaiting deposit (FY ${data?.fy || ""})`,
            href: "/reports/vendor-compliance/tds?deposited=no",
            icon: <Hourglass size={18} weight="fill" />,
            tone: (data?.pending_vendor_tds_deposits?.amount || 0) > 0 ? "warning" : "default",
            testId: "stat-pending-vendor-tds",
        },
        {
            eyebrow: "Outstanding Vendor Invoices",
            value: data?.outstanding_vendor_invoices?.amount,
            count: data?.outstanding_vendor_invoices?.count || 0,
            hint: "Unpaid bills + service invoices",
            href: "/vendors",
            icon: <Stack size={18} weight="fill" />,
            tone: (data?.outstanding_vendor_invoices?.amount || 0) > 0 ? "warning" : "default",
            testId: "stat-outstanding-vendor-invoices",
        },
    ];

    const toneCls = {
        indigo: "border-indigo-200 bg-indigo-50/40 hover:border-indigo-400",
        warning: "border-amber-200 bg-amber-50/40 hover:border-amber-400",
        default: "hover:border-slate-900",
    };
    const valueCls = {
        indigo: "text-indigo-700",
        warning: "text-amber-700",
        default: "text-slate-900",
    };

    return (
        <div className="space-y-3" data-testid="vendor-compliance-snapshot">
            <div className="reedos-eyebrow flex items-center gap-2">
                <CalendarBlank size={11} />
                Accounts · Vendor Compliance Snapshot
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {cards.map((c) => {
                    const tone = c.tone || "default";
                    const Body = (
                        <div className={"reedos-card p-4 group transition-colors border " + toneCls[tone]} data-testid={c.testId}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="reedos-eyebrow text-[10px]">{c.eyebrow}</div>
                                <div className="text-slate-400 group-hover:text-slate-900 transition-colors">{c.icon}</div>
                            </div>
                            <div className={"font-display font-bold reedos-data text-2xl mt-2 leading-none " + valueCls[tone]}>
                                {loading ? "—" : fmtINR(c.value)}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1.5">
                                {c.hint}
                                {c.count > 0 && (
                                    <span className="ml-1 text-slate-400">· {c.count} txn{c.count === 1 ? "" : "s"}</span>
                                )}
                            </div>
                        </div>
                    );
                    return (
                        <Link key={c.testId} to={c.href} className="block">{Body}</Link>
                    );
                })}
            </div>
        </div>
    );
}


function CycleStat({ label, value, delta }) {
    const fmt = (v) => v == null ? "—" : `${v}d`;
    let TrendIcon = Minus;
    let trendCls = "text-slate-400";
    let trendText = "—";
    if (delta != null) {
        if (delta < -0.05) { TrendIcon = TrendDown; trendCls = "text-emerald-600"; trendText = `${Math.abs(delta).toFixed(1)}d faster`; }
        else if (delta > 0.05) { TrendIcon = TrendUp; trendCls = "text-rose-600"; trendText = `${delta.toFixed(1)}d slower`; }
        else { trendText = "no change"; }
    }
    return (
        <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0" data-testid={`cycle-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="text-xs text-slate-600">{label}</span>
            <div className="flex items-center gap-2.5">
                <span className="reedos-data text-sm font-bold text-slate-900">{fmt(value)}</span>
                <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${trendCls}`}>
                    <TrendIcon size={11} weight="bold" /> {trendText}
                </span>
            </div>
        </div>
    );
}

function LifecycleHealthCard({ health }) {
    if (!health) return null;
    const cur = health.current_period || {};
    const prev = health.prev_period || {};
    const d = health.delta || {};
    const headline = cur.median_proposal_to_first_payment_days;
    const headlineDelta = d.median_proposal_to_first_payment_days;

    const hasData = (cur.sample_count ?? 0) > 0;
    return (
        <div className="reedos-card p-5" data-testid="lifecycle-health-card">
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Heartbeat size={12} weight="fill" /> Lifecycle health</div>
                    <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Proposal → Payment cycle</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">Median, last {health.window_days} days vs prior {health.window_days}.</p>
                </div>
                <Link to="/reports/aging" className="text-xs font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
                    Reports <ArrowUpRight size={12} weight="bold" />
                </Link>
            </div>

            {!hasData ? (
                <div className="text-sm text-slate-500 py-6 text-center">
                    Not enough data in the window to compute medians yet.
                </div>
            ) : (
                <>
                    <div className="rounded-md bg-gradient-to-br from-slate-900 to-slate-700 text-white p-4 mb-3" data-testid="lifecycle-health-headline">
                        <div className="reedos-eyebrow text-slate-300 text-[9px]">End-to-end median</div>
                        <div className="flex items-baseline gap-3 mt-1">
                            <span className="font-display text-3xl font-bold reedos-data leading-none">
                                {headline == null ? "—" : `${headline}d`}
                            </span>
                            {headlineDelta != null && (
                                <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${headlineDelta < 0 ? "text-emerald-300" : headlineDelta > 0 ? "text-rose-300" : "text-slate-300"}`}>
                                    {headlineDelta < 0 ? <TrendDown size={12} weight="bold" /> : headlineDelta > 0 ? <TrendUp size={12} weight="bold" /> : <Minus size={12} />}
                                    {headlineDelta === 0 ? "no change" : `${Math.abs(headlineDelta).toFixed(1)}d ${headlineDelta < 0 ? "faster" : "slower"}`}
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-1.5">
                            Across {cur.sample_count ?? 0} closed cycles · prior period {prev.sample_count ?? 0}
                        </div>
                    </div>
                    <div>
                        <CycleStat
                            label="Proposal → PO"
                            value={cur.median_proposal_to_po_days}
                            delta={d.median_proposal_to_po_days}
                        />
                        <CycleStat
                            label="PO finalize → first invoice"
                            value={cur.median_po_to_first_invoice_days}
                            delta={d.median_po_to_first_invoice_days}
                        />
                        <CycleStat
                            label="Invoice → first payment"
                            value={cur.median_invoice_to_first_payment_days}
                            delta={d.median_invoice_to_first_payment_days}
                        />
                    </div>
                </>
            )}
        </div>
    );
}


function RenewalsPipelineCard({ renewals, stale, loading }) {
    if (loading) {
        return (
            <div className="reedos-card p-5" data-testid="renewals-pipeline-card-loading">
                <div className="reedos-eyebrow text-slate-400">Renewals Pipeline</div>
                <div className="mt-2 text-sm text-slate-500">Loading…</div>
            </div>
        );
    }
    if (!renewals) return null;
    const c = renewals.counts || {};
    const totalActionable = (c.in_30 || 0) + (c.overdue || 0);
    const totalAhead = (c.in_60 || 0) + (c.in_90 || 0);
    const staleCount = (stale?.drafts_aged || 0) + (stale?.sent_aged || 0) + (stale?.overdue || 0);
    // Silent when there's literally nothing to surface — keep dashboard clean.
    if (totalActionable === 0 && totalAhead === 0 && (c.in_progress || 0) === 0 && staleCount === 0) {
        return null;
    }

    const Cell = ({ eyebrow, value, hint, tone, href, testId }) => {
        const toneClass =
            tone === "danger"
                ? "border-rose-200 bg-rose-50/60 hover:border-rose-400"
                : tone === "warning"
                    ? "border-amber-200 bg-amber-50/60 hover:border-amber-400"
                    : tone === "info"
                        ? "border-indigo-200 bg-indigo-50/60 hover:border-indigo-400"
                        : "border-slate-200 hover:border-slate-900";
        const valueClass =
            tone === "danger" ? "text-rose-700"
            : tone === "warning" ? "text-amber-700"
            : tone === "info" ? "text-indigo-700"
            : "text-slate-900";
        const Body = (
            <div
                className={`reedos-card p-4 transition-colors border ${toneClass}`}
                data-testid={testId}
            >
                <div className="reedos-eyebrow text-[10px]">{eyebrow}</div>
                <div className={`mt-2 reedos-data font-display font-bold text-2xl leading-none ${valueClass}`}>{value}</div>
                {hint && <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div>}
            </div>
        );
        return href ? <Link to={href} className="block">{Body}</Link> : Body;
    };

    return (
        <div className="reedos-card" data-testid="renewals-pipeline-card">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ArrowsClockwise size={18} className="text-indigo-600" />
                    <div>
                        <div className="reedos-eyebrow">Operational</div>
                        <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                            Renewals Pipeline
                        </h3>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {staleCount > 0 && (
                        <Link
                            to="/reports/stale-renewals"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:text-rose-900 hover:underline"
                            data-testid="stale-renewals-link"
                        >
                            {staleCount} stale · review →
                        </Link>
                    )}
                    <Link
                        to="/amc"
                        className="text-xs font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
                        data-testid="renewals-view-all"
                    >
                        View all AMCs <ArrowUpRight size={12} weight="bold" />
                    </Link>
                </div>
            </div>
            <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Cell
                    eyebrow="Overdue"
                    value={c.overdue ?? 0}
                    hint="end_date already passed"
                    tone={(c.overdue ?? 0) > 0 ? "danger" : undefined}
                    testId="renewals-bucket-overdue"
                />
                <Cell
                    eyebrow="Due in 30 days"
                    value={c.in_30 ?? 0}
                    hint="needs attention this month"
                    tone={(c.in_30 ?? 0) > 0 ? "warning" : undefined}
                    testId="renewals-bucket-in30"
                />
                <Cell
                    eyebrow="Due in 31–60 days"
                    value={c.in_60 ?? 0}
                    hint="plan this quarter"
                    testId="renewals-bucket-in60"
                />
                <Cell
                    eyebrow="Due in 61–90 days"
                    value={c.in_90 ?? 0}
                    hint="forward visibility"
                    testId="renewals-bucket-in90"
                />
                <Cell
                    eyebrow="In Progress"
                    value={c.in_progress ?? 0}
                    hint="renewal proposal initiated"
                    tone={(c.in_progress ?? 0) > 0 ? "info" : undefined}
                    testId="renewals-bucket-inprogress"
                />
            </div>
            <div className="px-5 pb-4 text-[11px] text-slate-500 flex items-center gap-2">
                <CalendarBlank size={12} />
                Default renewal lead window:{" "}
                <strong>{renewals.company_renewal_lead_days} days</strong> · As of{" "}
                {(renewals.as_of || "").slice(0, 10)}
            </div>
        </div>
    );
}



export default function Dashboard() {
    const { can, user } = useAuth();
    const showFinance = can("finance:read");
    const showSales = can("sales:read");
    const showCompliance = can("compliance:read");
    // UAT Obs 1 (2026-07-04) — DATA_SALES KPI tiles must match
    // reachable modules. Their allowed domain is Leads (Clients /
    // Proposals / POs sit behind SEC-02 chokepoint + Design 004
    // sidebar hides + Design 004-B route guards). This keeps the
    // Dashboard tile visibility in sync with those three existing
    // defense surfaces — no permission change, no schema change,
    // no API contract change.
    const isDataSales = user?.role === "data_sales";

    const [data, setData] = useState(null);
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [recentPayments, setRecentPayments] = useState([]);
    const [overdueInvoices, setOverdueInvoices] = useState([]);
    const [health, setHealth] = useState(null);
    // C.3 Commit 5 — Renewals Pipeline widget data.
    const [renewals, setRenewals] = useState(null);
    const [staleTotals, setStaleTotals] = useState(null);
    // Iter 79.3 — Vendor-side compliance dashboard cards (Accounts snapshot).
    const [vendorCompliance, setVendorCompliance] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                // Iter 65 — Sales-mode dashboard skips the finance endpoints
                // entirely. The backend also enforces this (`finance:read`
                // required on /invoice/list + /payment/history) so even a
                // hand-crafted fetch would receive a 403 — this branch just
                // avoids the wasted round-trip and a noisy console error.
                const baseCalls = [
                    api.get("/billing/dashboard/summary"),
                    api.get("/billing/reports/lifecycle-health").catch(() => ({ data: null })),
                    api.get("/billing/reports/renewals-summary").catch(() => ({ data: null })),
                    api.get("/billing/reports/stale-renewals").catch(() => ({ data: null })),
                ];
                const financeCalls = showFinance ? [
                    api.get("/billing/invoice/list").catch(() => ({ data: { items: [] } })),
                    api.get("/billing/payment/history").catch(() => ({ data: { items: [] } })),
                    api.get("/billing/invoice/list", { params: { overdue: true, sort: "overdue_desc" } }).catch(() => ({ data: { items: [] } })),
                ] : [];
                const complianceCalls = (showFinance && showCompliance) ? [
                    api.get("/billing/reports/vendor-compliance/dashboard-summary").catch(() => ({ data: null })),
                ] : [];
                const responses = await Promise.all([...baseCalls, ...financeCalls, ...complianceCalls]);
                const [s, lh, rs, sr, ...rest] = responses;
                setData(s.data);
                setHealth(lh.data);
                setRenewals(rs.data);
                setStaleTotals(sr.data?.totals || null);
                let idx = 0;
                if (showFinance) {
                    const inv = rest[idx++];
                    const pay = rest[idx++];
                    const overdue = rest[idx++];
                    setRecentInvoices(inv.data.items.slice(0, 5));
                    setRecentPayments(pay.data.items.slice(0, 5));
                    setOverdueInvoices(overdue.data.items.slice(0, 5));
                }
                if (showFinance && showCompliance) {
                    const vc = rest[idx++];
                    setVendorCompliance(vc.data);
                }
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load dashboard"));
            } finally {
                setLoading(false);
            }
        })();
    }, [showFinance, showCompliance]);

    const overdueCount = data?.overdue?.count ?? 0;
    // SEC-02 / DASH-CONSISTENCY (2026-02) — explicit sales:read gate.
    // Previously: `(view_mode==="sales") || !showFinance` — which would
    // render the sales-shaped commercial pipeline to ANY user lacking
    // finance:read, including custom read-only roles that legitimately
    // have neither sales:read nor finance:read. The frontend route
    // guard (RequirePermAny) keeps such users off the page entirely;
    // this check is the second line of defence for any user who DOES
    // hold finance:read alone (e.g., Accounts) — they should always
    // see the finance view, never the sales-pipeline view.
    const isSalesView = ((data?.view_mode === "sales") || !showFinance) && showSales;

    return (
        <div data-testid="dashboard-page">
            <PageHeader
                eyebrow={isSalesView ? "Phase 1 · Your pipeline" : "Phase 1"}
                title={isSalesView ? "Pipeline Overview" : "Commercial Overview"}
                description={
                    isSalesView
                        ? "Snapshot of records assigned to you across Leads, Proposals, Purchase Orders and conversion velocity. Counts mirror the list pages — only your records, not the global pipeline."
                        : "Real-time snapshot across Purchase Orders, Invoices, Payments and overdue receivables."
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-8">
                {user?.is_employee_linked && <MonthSummaryCard />}
                {isSalesView ? (
                    /* Iter 65 — Sales dashboard: leads / proposals / PO / clients.
                       Zero money tiles, zero recent invoices/payments.
                       UAT Obs 1 (2026-07-04) — DATA_SALES sees only the
                       Leads tile; the remaining four tiles map to modules
                       already blocked by SEC-02 + Design 004 + 004-B. */
                    <div
                        className={`grid gap-4 ${
                            isDataSales
                                ? "grid-cols-1 sm:grid-cols-1 max-w-sm"
                                : "grid-cols-2 lg:grid-cols-5"
                        }`}
                        data-testid="sales-kpi-grid"
                    >
                        <Stat
                            eyebrow="Leads"
                            value={loading ? "—" : data?.leads?.total ?? 0}
                            hint={`${data?.leads?.active ?? 0} active`}
                            icon={<UserCircle size={18} />}
                            href="/leads"
                            testId="stat-leads"
                            valueClass="text-3xl"
                        />
                        {!isDataSales && (
                            <>
                                <Stat
                                    eyebrow="Clients"
                                    value={loading ? "—" : data?.clients ?? 0}
                                    hint="onboarded organisations"
                                    icon={<Users size={18} />}
                                    href="/clients"
                                    testId="stat-clients"
                                    valueClass="text-3xl"
                                />
                                <Stat
                                    eyebrow="Proposals"
                                    value={loading ? "—" : data?.proposals?.total ?? 0}
                                    hint={`${data?.proposals?.open ?? 0} open · ${data?.proposals?.finalized ?? 0} finalized`}
                                    icon={<Presentation size={18} />}
                                    href="/proposals"
                                    testId="stat-proposals"
                                    valueClass="text-3xl"
                                />
                                <Stat
                                    eyebrow="Total POs"
                                    value={loading ? "—" : data?.po?.total ?? 0}
                                    hint={`${data?.po?.open ?? 0} open · ${data?.po?.finalized ?? 0} finalized`}
                                    icon={<FileText size={18} />}
                                    href="/purchase-orders"
                                    testId="stat-total-pos"
                                    valueClass="text-3xl"
                                />
                                <Stat
                                    eyebrow="Finalized POs"
                                    value={loading ? "—" : data?.po?.finalized ?? 0}
                                    hint="locked & invoiceable"
                                    icon={<LockKey size={18} weight="fill" />}
                                    href="/purchase-orders?status=finalized"
                                    testId="stat-finalized-pos"
                                    valueClass="text-3xl"
                                />
                            </>
                        )}
                    </div>
                ) : (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    <Stat
                        eyebrow="Total POs"
                        value={loading ? "—" : data?.po.total ?? 0}
                        hint={`${data?.po.open ?? 0} open · ${data?.po.finalized ?? 0} finalized`}
                        icon={<FileText size={18} />}
                        href="/purchase-orders"
                        testId="stat-total-pos"
                        valueClass="text-3xl"
                    />
                    <Stat
                        eyebrow="Finalized POs"
                        value={loading ? "—" : data?.po.finalized ?? 0}
                        hint="locked & invoiceable"
                        icon={<LockKey size={18} weight="fill" />}
                        href="/purchase-orders?status=finalized"
                        testId="stat-finalized-pos"
                        valueClass="text-3xl"
                    />
                    <Stat
                        eyebrow="Invoices"
                        value={loading ? "—" : data?.invoice.total ?? 0}
                        hint={
                            <span className="flex flex-wrap gap-1 items-center">
                                <StatusBadge status="Unpaid" /> {data?.invoice.counts?.Unpaid ?? 0}
                                <StatusBadge status="Partial" /> {data?.invoice.counts?.Partial ?? 0}
                                <StatusBadge status="Paid" /> {data?.invoice.counts?.Paid ?? 0}
                                {Number(data?.invoice.counts?.["Fully Settled — Pending TDS Attachment"]) > 0 && (
                                    <>
                                        <StatusBadge status="Fully Settled — Pending TDS Attachment" compact /> {data.invoice.counts["Fully Settled — Pending TDS Attachment"]}
                                    </>
                                )}
                            </span>
                        }
                        icon={<Receipt size={18} />}
                        href="/invoices"
                        testId="stat-invoices"
                        valueClass="text-3xl"
                    />
                    <Stat
                        eyebrow="Collection Outstanding"
                        value={loading ? "—" : formatINR(data?.money.total_collection_outstanding ?? data?.money.total_outstanding ?? 0, { compact: true })}
                        hint={
                            Number(data?.money?.total_pending_tds_reconciliation) > 0.01 ? (
                                <span className="text-[11px]">
                                    Collected {formatINR(data?.money.total_collected ?? 0, { compact: true })} of {formatINR(data?.money.total_invoiced ?? 0, { compact: true })}
                                    <span className="block text-teal-700 font-medium mt-0.5" title="TDS in lifecycle Deducted/Received — not a client receivable.">
                                        + {formatINR(data.money.total_pending_tds_reconciliation, { compact: true })} pending TDS reconciliation
                                    </span>
                                </span>
                            ) : (
                                `Collected ${formatINR(data?.money.total_collected ?? 0, { compact: true })} of ${formatINR(data?.money.total_invoiced ?? 0, { compact: true })}`
                            )
                        }
                        icon={<HandCoins size={18} />}
                        href="/payments"
                        testId="stat-outstanding"
                        valueClass="text-3xl"
                    />
                    <Stat
                        eyebrow="TDS Receivable"
                        value={loading ? "—" : formatINR(data?.money.tds_receivable ?? 0, { compact: true })}
                        hint="Tax credit (Form 16A pending verification)"
                        icon={<HandCoins size={18} />}
                        href="/reports/compliance/tds"
                        testId="stat-tds-receivable"
                        valueClass="text-3xl"
                        tone={data?.money?.tds_receivable > 0 ? "warning" : undefined}
                    />
                    <Stat
                        eyebrow="Overdue"
                        value={loading ? "—" : overdueCount}
                        hint={
                            overdueCount > 0 ? (
                                <span className="text-rose-700 font-semibold">
                                    {formatINR(data?.overdue?.amount ?? 0, { compact: true })} at risk
                                </span>
                            ) : "All invoices on track"
                        }
                        icon={<WarningOctagon size={18} weight={overdueCount > 0 ? "fill" : "regular"} />}
                        href="/invoices?overdue=true"
                        testId="overdue-tile"
                        valueClass={"text-3xl " + (overdueCount > 0 ? "text-rose-700" : "")}
                        tone={overdueCount > 0 ? "danger" : undefined}
                    />
                </div>
                )}

                {/* Iter 79.3 — Vendor-side Accounts compliance snapshot. */}
                {!isSalesView && showCompliance && (
                    <VendorComplianceCards data={vendorCompliance} loading={loading} />
                )}

                {/* Overdue panel — only shows if there are overdue items.
                    Iter 65 — finance-only (hidden from Sales view). */}
                {!isSalesView && overdueInvoices.length > 0 && (
                    <div className="reedos-card border-rose-200" data-testid="overdue-panel">
                        <div className="px-5 py-4 border-b border-rose-200 bg-rose-50/50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <WarningOctagon size={18} weight="fill" className="text-rose-600" />
                                <div>
                                    <div className="reedos-eyebrow text-rose-800">Needs attention</div>
                                    <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Overdue invoices</h3>
                                </div>
                            </div>
                            <Link to="/invoices?overdue=true" className="text-xs font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
                                View all <ArrowUpRight size={12} weight="bold" />
                            </Link>
                        </div>
                        <table className="w-full text-sm">
                            <tbody>
                                {overdueInvoices.map((i) => (
                                    <tr key={i.id} className="border-t border-rose-100 hover:bg-rose-50/30" data-testid={`overdue-row-${i.id}`}>
                                        <td className="px-5 py-3">
                                            <Link to={`/invoices/${i.id}`} className="font-mono text-xs font-semibold text-slate-900 hover:underline">
                                                {i.invoice_number}
                                            </Link>
                                            <div className="text-xs text-slate-500 mt-0.5">{i.client?.name}</div>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold border bg-rose-50 text-rose-700 border-rose-200">
                                                <Clock size={10} weight="fill" /> {i.days_overdue} days overdue
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-right reedos-data text-sm font-semibold text-rose-700">{formatINR(i.outstanding)}</td>
                                        <td className="px-5 py-3 text-right text-[11px] text-slate-500">due {formatDate(i.due_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* C.3 Commit 5 — Renewals Pipeline widget. Shown to all
                    operators (sales + finance) since renewal awareness
                    is a flat operational concern. */}
                <RenewalsPipelineCard renewals={renewals} stale={staleTotals} loading={loading} />


                <div className={isSalesView ? "grid lg:grid-cols-1 gap-6" : "grid lg:grid-cols-3 gap-6"}>
                    <LifecycleHealthCard health={health} />

                    {/* Iter 65 — Recent Invoices + Recent Payments widgets
                        are finance-only. Sales view collapses the row to a
                        single-column grid showing just the lifecycle card. */}
                    {!isSalesView && (
                    <div className="reedos-card" data-testid="recent-invoices-card">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                            <div>
                                <div className="reedos-eyebrow">Recent invoices</div>
                                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Last issued</h3>
                            </div>
                            <Link to="/invoices" className="text-xs font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
                                View all <ArrowUpRight size={12} weight="bold" />
                            </Link>
                        </div>
                        {recentInvoices.length === 0 ? (
                            <div className="px-5 py-10 text-center text-sm text-slate-500">No invoices yet.</div>
                        ) : (
                            <table className="w-full text-sm">
                                <tbody>
                                    {recentInvoices.map((i) => (
                                        <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
                                            <td className="px-5 py-3">
                                                <Link to={`/invoices/${i.id}`} className="font-mono text-xs text-slate-900 font-medium">{i.invoice_number}</Link>
                                                <div className="text-xs text-slate-500 mt-0.5">{i.client?.name}</div>
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <div className="reedos-data text-sm font-semibold">{formatINR(i.total_amount)}</div>
                                                <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(i.invoice_date)}</div>
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <StatusBadge status={i.status} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    )}

                    {!isSalesView && (
                    <div className="reedos-card" data-testid="recent-payments-card">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                            <div>
                                <div className="reedos-eyebrow">Recent payments</div>
                                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Receipts</h3>
                            </div>
                            <Link to="/payments" className="text-xs font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
                                View all <ArrowUpRight size={12} weight="bold" />
                            </Link>
                        </div>
                        {recentPayments.length === 0 ? (
                            <div className="px-5 py-10 text-center text-sm text-slate-500">No payments recorded.</div>
                        ) : (
                            <table className="w-full text-sm">
                                <tbody>
                                    {recentPayments.map((p) => (
                                        <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                                            <td className="px-5 py-3">
                                                <div className="font-mono text-xs text-slate-900 font-medium">{p.invoice_number}</div>
                                                <div className="text-xs text-slate-500 mt-0.5">{p.client?.name} · {p.payment_mode}</div>
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <div className="reedos-data text-sm font-semibold text-emerald-700">{formatINR(p.amount)}</div>
                                                <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(p.payment_date)}</div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
}
